import {useData} from "@hps/hops-react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Collapse from "@mui/material/Collapse";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
	filterAdornmentButtonStyle,
	filterControlStyle,
	filterRowSx
} from "Raildays/Components/RaildaysFilterStyles.js";
import RaildaysMap from "Raildays/Components/RaildaysMap.js";
import RaildaysScrollTopButton from "Raildays/Components/RaildaysScrollTopButton.js";
import {
	formatDistanceMilesKm,
	resolveExternalUrl,
	resolveRailwayMediaUrl,
	resolveRailwayTypeColor,
	slugify
} from "Raildays/RaildaysUtils.js";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useOutletContext, useSearchParams} from "react-router-dom";

import Button from "Components/Button.js";
import Card from "Components/Card.js";
import CardSection from "Components/Container.js";
import IconButton from "Components/IconButton.js";
import Image from "Components/Image.js";
import Link from "Components/Link.js";
import Loader from "Components/Loader.js";
import Select from "Components/Select.js";
import TextField from "Components/TextField.js";
import RaildaysService from "Services/RaildaysService.js";

import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import XIcon from "@mui/icons-material/X";

const MAP_HEIGHT = "calc(100vh - var(--raildays-content-top))";
const CARD_BODY_PADDING = "clamp(16px, 2.2vw, 32px)";
const TYPE_COLOR_PALETTE = [
	"#e11d48",
	"#2563eb",
	"#16a34a",
	"#ea580c",
	"#7c3aed",
	"#0f766e",
	"#b45309",
	"#be185d",
	"#1d4ed8",
	"#15803d"
];

const normalizePostcode = value => (value || "").toString().toUpperCase().replace(/\s+/g, "");

const haversineMiles = (pointA, pointB) => {
	if (!pointA || !pointB) return null;
	const toRad = deg => (deg * Math.PI) / 180;
	const lat1 = pointA.lat;
	const lon1 = pointA.lng;
	const lat2 = pointB.lat;
	const lon2 = pointB.lng;
	if ([lat1, lon1, lat2, lon2].some(val => Number.isNaN(val))) return null;
	const R = 3958.8;
	const dLat = toRad(lat2 - lat1);
	const dLon = toRad(lon2 - lon1);
	const a =
		Math.sin(dLat / 2) ** 2 +
		Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
};

const parseISODate = value => {
	if (!value) return null;
	const parts = value.split("-");
	if (parts.length < 3) return null;
	const [year, month, day] = parts.map(Number);
	if (!year || !month || !day) return null;
	return new Date(Date.UTC(year, month - 1, day));
};

const formatOrdinal = value => {
	const mod10 = value % 10;
	const mod100 = value % 100;
	if (mod10 === 1 && mod100 !== 11) return `${value}st`;
	if (mod10 === 2 && mod100 !== 12) return `${value}nd`;
	if (mod10 === 3 && mod100 !== 13) return `${value}rd`;
	return `${value}th`;
};

const formatDateLong = date => {
	if (!date) return "";
	const day = formatOrdinal(date.getUTCDate());
	const month = date.toLocaleString("en-GB", {month: "long", timeZone: "UTC"});
	const year = date.getUTCFullYear();
	return `${day} ${month} ${year}`;
};

const buildMonthOptions = () => {
	const results = [];
	const start = new Date();
	start.setUTCDate(1);
	start.setUTCHours(0, 0, 0, 0);
	const end = new Date(Date.UTC(start.getUTCFullYear() + 1, 11, 1));

	const runner = new Date(start);
	while (runner <= end) {
		const year = runner.getUTCFullYear();
		const month = runner.getUTCMonth() + 1;
		const slug = `${year}-${String(month).padStart(2, "0")}`;
		const label = runner.toLocaleString("en-GB", {month: "short", year: "numeric", timeZone: "UTC"});
		results.push({slug, label});
		runner.setUTCMonth(runner.getUTCMonth() + 1);
	}
	return results;
};

const resolveItemName = item => (
	item?.Name || "Untitled"
);

const resolveItemDescription = item => (
	item?.Description || ""
);

const resolveItemText = item => (
	item?.Text || ""
);

const resolveItemImage = item => (
	resolveRailwayMediaUrl(item?.Image || "")
);

const resolveItemLocation = item => {
	const address = item?.Address;
	if (!address) return "";
	const parts = [address.City, address.County, address.Country]
		.filter(Boolean);
	return parts.join(", ");
};

const resolveItemRailwayName = item => (
	item?.Railway?.Name ||
	item?.RailwayName ||
	""
);

const resolveItemOrgId = item => (
	item?.OrgId || ""
);

const resolveItemId = item => (
	item?.EventId || item?.Id || slugify(resolveItemName(item))
);

const resolveItemLink = item => `/events/${resolveItemId(item)}`;

const resolveItemAddress = item => {
	const address = item?.Address;
	if (!address) return "";
	return [
		address.Address,
		address.City,
		address.County,
		address.Postcode,
		address.Country
	].filter(Boolean).join(", ");
};

const resolveItemLatLng = item => {
	const location = item?.Location;
	const latitude = Number(location?.Latitude);
	const longitude = Number(location?.Longitude);
	if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;
	return {lat: latitude, lng: longitude};
};

const resolveItemMapMarkerId = item => {
	const itemId = resolveItemId(item);
	const coords = resolveItemLatLng(item);
	if (!coords) return String(itemId);
	return `${itemId}-${coords.lat}-${coords.lng}`;
};

const resolveEventTypes = item => {
	if (Array.isArray(item?.EventTypes)) return item.EventTypes;
	return [];
};

const resolveMarkerImage = item => (
	resolveRailwayMediaUrl(
		item?.Railway?.Logo ||
		item?.Logo ||
		item?.Image
	)
);

const resolveTypeSlug = type => (
	type?.Slug || slugify(type?.Name || "")
);

const resolveTypeName = type => (
	type?.Name || ""
);

const normaliseHexColor = value => {
	if (!value) return "";
	const trimmed = String(value).trim();
	if (/^#[0-9a-f]{6}$/i.test(trimmed)) return trimmed;
	if (/^#[0-9a-f]{3}$/i.test(trimmed)) {
		const short = trimmed.replace("#", "");
		return `#${short[0]}${short[0]}${short[1]}${short[1]}${short[2]}${short[2]}`;
	}
	if (/^[0-9a-f]{6}$/i.test(trimmed)) return `#${trimmed}`;
	return "";
};

const hashString = value => (
	String(value || "").split("").reduce((total, character) => total + character.charCodeAt(0), 0)
);

const resolveSeedColor = value => (
	TYPE_COLOR_PALETTE[hashString(value) % TYPE_COLOR_PALETTE.length]
);

const resolveTypeColor = type => {
	const explicit = normaliseHexColor(type?.Colour);
	if (explicit) return explicit;
	const seed = resolveTypeSlug(type) || resolveTypeName(type) || type?.Id || "event";
	return resolveSeedColor(seed);
};

const resolveContrastText = color => {
	const safeColor = normaliseHexColor(color);
	if (!safeColor) return "#ffffff";
	const normalized = safeColor.replace("#", "");
	const r = parseInt(normalized.substring(0, 2), 16) || 0;
	const g = parseInt(normalized.substring(2, 4), 16) || 0;
	const b = parseInt(normalized.substring(4, 6), 16) || 0;
	const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
	return luminance > 0.64 ? "#111827" : "#ffffff";
};

const resolveItemAccentColor = (item, railwayTypeColor = "") => {
	const types = resolveEventTypes(item);
	const explicitEventTypeColor = normaliseHexColor(types[0]?.Colour);
	if (explicitEventTypeColor) return explicitEventTypeColor;

	const typeColor = normaliseHexColor(railwayTypeColor);
	if (typeColor) return typeColor;

	const railwayMap = item?.Railway?.Map;
	const railwayColor = normaliseHexColor(railwayMap?.Colour);
	if (railwayColor) return railwayColor;

	if (types.length) return resolveTypeColor(types[0]);

	return resolveSeedColor(resolveItemId(item));
};

const resolveMarkerColor = (item, railwayTypeColor = "") => {
	return resolveItemAccentColor(item, railwayTypeColor);
};

const resolveExternalLink = item => resolveExternalUrl(
	item?.Website ||
	item?.Url ||
	item?.Contact?.Website ||
	item?.Contact?.Url ||
	""
);

const resolveSocialUrl = (base, handle) => {
	if (!handle) return "";
	if (handle.startsWith("http")) return handle;
	return `${base}${handle.replace(/^@/, "")}`;
};

const resolveSocialLinks = item => {
	const social = item?.Railway?.SocialMedia || item?.SocialMedia;
	if (!social) return {};
	return {
		twitter: resolveSocialUrl("https://twitter.com/", social?.Twitter),
		facebook: resolveSocialUrl("https://facebook.com/", social?.Facebook),
		instagram: resolveSocialUrl("https://instagram.com/", social?.Instagram)
	};
};

const resolveEventDateRange = item => {
	const start = parseISODate(item?.StartDate);
	const end = parseISODate(item?.EndDate);
	return {start, end: end || start};
};

const resolveMonthMatch = (range, monthSlug) => {
	if (!monthSlug || monthSlug === "All") return true;
	if (!range?.start || !range?.end) return false;
	const [year, month] = monthSlug.split("-").map(Number);
	if (!year || !month) return true;
	const monthStart = new Date(Date.UTC(year, month - 1, 1));
	const monthEnd = new Date(Date.UTC(year, month, 0));
	return range.start <= monthEnd && range.end >= monthStart;
};

const RaildaysEventsView = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [typeFilter, setTypeFilter] = useState(searchParams.get("type") || "All");
	const [monthFilter, setMonthFilter] = useState(searchParams.get("date") || "All");

	const [postcode, setPostcode] = useState("");
	const [postcodeError, setPostcodeError] = useState("");
	const locationContext = useOutletContext() || {};
	const targetLocation = locationContext.targetLocation || null;
	const setTargetLocation = locationContext.setTargetLocation || (() => {});
	const locationLoading = Boolean(locationContext.locationLoading);
	const setLocationLoading = locationContext.setLocationLoading || (() => {});
	const requestCurrentLocation = locationContext.requestCurrentLocation || (() => {});

	const mapRef = useRef(null);
	const markerRefs = useRef({});
	const monthBarRef = useRef(null);
	const [quickMonthCount, setQuickMonthCount] = useState(6);

	const monthOptions = useMemo(() => buildMonthOptions(), []);
	const quickMonthOptions = useMemo(
		() => monthOptions.slice(0, Math.max(1, quickMonthCount)),
		[monthOptions, quickMonthCount]
	);
	const overflowMonthOptions = useMemo(
		() => monthOptions.slice(Math.max(1, quickMonthCount)),
		[monthOptions, quickMonthCount]
	);
	const overflowMonthValue = useMemo(() => {
		if (monthFilter === "All") return "__more__";
		if (quickMonthOptions.some(option => option.slug === monthFilter)) return "__more__";
		if (overflowMonthOptions.some(option => option.slug === monthFilter)) return monthFilter;
		return "__more__";
	}, [monthFilter, overflowMonthOptions, quickMonthOptions]);

	useEffect(() => {
		const calculateQuickMonthCount = () => {
			const barWidth = monthBarRef.current?.clientWidth || 0;
			if (!barWidth) return;

			// Reserve room for "All Dates" and the overflow dropdown on desktop.
			const isDesktop = window.innerWidth >= 900;
			const reservedWidth = isDesktop ? 330 : 120;
			const perMonthWidth = 96;
			const availableWidth = Math.max(0, barWidth - reservedWidth);
			const nextCount = Math.max(1, Math.min(monthOptions.length, Math.floor(availableWidth / perMonthWidth)));
			setQuickMonthCount(prev => (prev === nextCount ? prev : nextCount));
		};

		calculateQuickMonthCount();
		window.addEventListener("resize", calculateQuickMonthCount, {passive: true});

		let resizeObserver;
		if ((typeof ResizeObserver !== "undefined") && monthBarRef.current) {
			resizeObserver = new ResizeObserver(calculateQuickMonthCount);
			resizeObserver.observe(monthBarRef.current);
		}

		return () => {
			window.removeEventListener("resize", calculateQuickMonthCount);
			resizeObserver?.disconnect();
		};
	}, [monthOptions.length]);

	useEffect(() => {
		setTypeFilter(searchParams.get("type") || "All");
		setMonthFilter(searchParams.get("date") || "All");
	}, [searchParams]);

	const apiFilterParams = useMemo(() => {
		const params = {};
		if (typeFilter !== "All") params.Type = typeFilter;
		if (monthFilter !== "All") params.Date = monthFilter;
		return params;
	}, [monthFilter, typeFilter]);

	const {data, error, loading} = useData(useCallback(
		() => RaildaysService.getEvents(Object.keys(apiFilterParams).length ? apiFilterParams : undefined),
		[apiFilterParams]
	));
	const {data: railwaysData} = useData(useCallback(
		() => RaildaysService.getRailways(),
		[]
	));

	const items = useMemo(() => {
		const resolved = data;
		if (!resolved) return [];
		if (Array.isArray(resolved)) return resolved;
		if (Array.isArray(resolved.objects)) return resolved.objects;
		return [];
	}, [data]);

	const railways = useMemo(() => {
		const resolved = railwaysData;
		if (!resolved) return [];
		if (Array.isArray(resolved)) return resolved;
		if (Array.isArray(resolved.objects)) return resolved.objects;
		if (Array.isArray(resolved.Objects)) return resolved.Objects;
		return [];
	}, [railwaysData]);

	const railwaysByOrgId = useMemo(() => {
		const index = new Map();
		railways.forEach(railway => {
			const orgId = railway?.Id;
			if ((orgId === undefined) || (orgId === null)) return;
			index.set(String(orgId), {
				name: railway?.Name || "",
				typeColor: resolveRailwayTypeColor(railway)
			});
		});
		return index;
	}, [railways]);

	const filteredByCategory = useMemo(() => {
		return items.filter(item => Number(item?.Category) !== 3);
	}, [items]);

	const filteredItems = useMemo(() => {
		return filteredByCategory.filter(item => {
			const typeMatches =
				(typeFilter === "All") ||
				resolveEventTypes(item).some(type => resolveTypeSlug(type) === typeFilter);
			if (!typeMatches) return false;

			const dateRange = resolveEventDateRange(item);
			return resolveMonthMatch(dateRange, monthFilter);
		});
	}, [filteredByCategory, monthFilter, typeFilter]);

	const typeOptions = useMemo(() => {
		const map = new Map();
		filteredByCategory.forEach(item => {
			resolveEventTypes(item).forEach(type => {
				const name = resolveTypeName(type);
				const slug = resolveTypeSlug(type);
				if (!slug || !name) return;
				if (!map.has(slug)) {
					map.set(slug, {
						id: type?.Id || slug,
						name,
						slug,
						color: resolveTypeColor(type)
					});
				}
			});
		});
		return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
	}, [filteredByCategory]);

	const mapLegendItems = useMemo(
		() => typeOptions.map(type => ({label: type.name, color: type.color})),
		[typeOptions]
	);

	const itemsWithDistance = useMemo(() => {
		if (!targetLocation) return filteredItems;
		return filteredItems
			.map(item => {
				const coords = resolveItemLatLng(item);
				const distance = coords ? haversineMiles(targetLocation, coords) : null;
				return {...item, __distance: distance};
			})
			.sort((a, b) => {
				if (a.__distance === null && b.__distance === null) return 0;
				if (a.__distance === null) return 1;
				if (b.__distance === null) return -1;
				return a.__distance - b.__distance;
			});
	}, [filteredItems, targetLocation]);

	const mapMarkers = useMemo(() => {
		const dedupe = new Set();
		return itemsWithDistance
			.map(item => {
				const coords = resolveItemLatLng(item);
				if (!coords) return null;
				const markerId = resolveItemMapMarkerId(item);
				if (dedupe.has(markerId)) return null;
				dedupe.add(markerId);
				const itemOrgId = resolveItemOrgId(item);
				const railwayMeta = railwaysByOrgId.get(String(itemOrgId || ""));
				const railwayName =
					resolveItemRailwayName(item) ||
					railwayMeta?.name ||
					"";
				const location = resolveItemLocation(item);
				const dateRange = resolveEventDateRange(item);
				const dateLabel = dateRange.start && dateRange.end ?
					(dateRange.start.getTime() === dateRange.end.getTime() ?
						formatDateLong(dateRange.start) :
						`${formatDateLong(dateRange.start)} - ${formatDateLong(dateRange.end)}`) :
					"";
				const typeLabel = resolveEventTypes(item)
					.map(resolveTypeName)
					.filter(Boolean)
					.join(", ");
				const distanceLabel =
					(typeof item?.__distance === "number" && !Number.isNaN(item.__distance)) ?
						formatDistanceMilesKm(item.__distance) :
						"";
				return {
					id: markerId,
					name: resolveItemName(item),
					railwayName,
					location,
					subname: [railwayName, location].filter(Boolean).join(", "),
					dateLabel,
					typeLabel,
					distanceLabel,
					position: [coords.lat, coords.lng],
					color: resolveMarkerColor(item, railwayMeta?.typeColor),
					image: resolveMarkerImage(item),
					actionLabel: "View event",
					link: resolveItemLink(item)
				};
			})
			.filter(Boolean);
	}, [itemsWithDistance, railwaysByOrgId]);

	const mapBounds = useMemo(() => mapMarkers.map(marker => marker.position), [mapMarkers]);

	const handleViewOnMap = useCallback(item => {
		const id = resolveItemMapMarkerId(item);
		const marker = markerRefs.current[id];
		if (!marker) return;
		marker.openPopup();
		const map = mapRef.current;
		if (map) {
			map.setView(marker.getLatLng(), Math.max(map.getZoom(), 9), {animate: true});
		}
	}, []);

	const handleUseLocation = useCallback(() => {
		setPostcodeError("");
		requestCurrentLocation();
	}, [requestCurrentLocation]);

	const handlePostcodeLookup = useCallback(async () => {
		const place = normalizePostcode(postcode);
		if (!place) {
			setPostcodeError("Please enter a postcode.");
			return;
		}

		setLocationLoading(true);
		setPostcodeError("");

		try {
			const response = await fetch(`https://api.getthedata.com/postcode/${place}`);
			const dataResponse = await response.json();
			const latitude = Number(dataResponse?.data?.latitude);
			const longitude = Number(dataResponse?.data?.longitude);

			if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
				setPostcodeError("We could not find that postcode.");
				setLocationLoading(false);
				return;
			}

			setTargetLocation({lat: latitude, lng: longitude, label: place});
		}
		catch (lookupError) {
			setPostcodeError("We could not look up that postcode.");
		}
		finally {
			setLocationLoading(false);
		}
	}, [postcode]);

	const handleTypeChange = value => {
		setTypeFilter(value);
		setSearchParams(prev => {
			const next = new URLSearchParams(prev);
			if (value === "All") {
				next.delete("type");
			}
			else {
				next.set("type", value);
			}
			return next;
		});
	};

	const handleMonthChange = value => {
		setMonthFilter(value);
		setSearchParams(prev => {
			const next = new URLSearchParams(prev);
			if (value === "All") {
				next.delete("date");
			}
			else {
				next.set("date", value);
			}
			return next;
		});
	};
	const handleOverflowMonthChange = value => {
		if (!value || value === "__more__") return;
		handleMonthChange(value);
	};

	const headline = "Events";
	const description = "There's plenty to see, do and enjoy at heritage railway events around the UK. Many railways also offer experience days such as driving a train or dining experiences.";

	return (
		<Box
			component="main"
			sx={{
				flexGrow: 1,
				mx: {xs: 2, md: 3, lg: 5},
				mb: {xs: 2, md: 3, lg: 5},
				backgroundColor: "#fff",
				borderRadius: 2
			}}
		>
			<Container maxWidth={false} sx={{py: {xs: 4, md: 6}}}>
				<Box sx={{px: {xs: 2, md: 4, lg: 6}}}>
					<Breadcrumbs sx={{textTransform: "uppercase", color: "#e2b534"}}>
						<Button label="Home" uri="/" style={{color: "#e2b534"}} variant="text" />
						<Typography color="#e2b534">{headline}</Typography>
					</Breadcrumbs>
					<Typography variant="h4" sx={{mt: 2, fontWeight: 800, textTransform: "uppercase"}}>
						{headline}
					</Typography>
				</Box>

				<Box sx={{px: {xs: 2, md: 4, lg: 6}, mt: 2}}>
					<Grid container spacing={3} alignItems="center">
						<Grid item xs={12} md={6}>
							<Typography>{description}</Typography>
						</Grid>
						<Grid item xs={12} md={6}>
							<Box sx={filterRowSx}>
								<Box sx={{width: {xs: "100%", md: 220}}}>
									<Select
										options={[
											{label: "All", value: "All"},
											...typeOptions.map(type => ({label: type.name, value: type.slug}))
										]}
										size="small"
										value={typeFilter}
										onChange={handleTypeChange}
										style={{...filterControlStyle, width: "100%"}}
									/>
								</Box>

								<Box
									component="form"
									onSubmit={event => {
										event.preventDefault();
										handlePostcodeLookup();
									}}
									sx={{
										display: "flex",
										flexDirection: "row",
										gap: 1,
										alignItems: "center"
									}}
								>
									<Box sx={{width: {xs: "100%", md: 220}}}>
										<TextField
											placeholder="Enter Postcode"
											value={postcode}
											onChange={value => setPostcode(value)}
											controlled={true}
											inputProps={{"aria-label": "Enter postcode", "style": {textAlign: "right"}}}
											size="small"
											style={{...filterControlStyle, width: "100%"}}
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<IconButton
															onClick={handleUseLocation}
															disabled={locationLoading}
															size="small"
															label="Use my location"
															style={filterAdornmentButtonStyle}
															icon={LocationOnIcon} />
													</InputAdornment>
												),
												endAdornment: (
													<InputAdornment position="end">
														<IconButton
															disabled={locationLoading}
															size="small"
															label="Find nearby"
															onClick={handlePostcodeLookup}
															style={filterAdornmentButtonStyle}
															icon={MyLocationIcon} />
													</InputAdornment>
												)
											}}
										/>
									</Box>
								</Box>
							</Box>
						</Grid>
					</Grid>

					<Collapse in={!!postcodeError}>
						{postcodeError && (
							<Box sx={{mt: 2}}>
								<Alert severity="error">{postcodeError}</Alert>
							</Box>
						)}
					</Collapse>
				</Box>

				<Box sx={{backgroundColor: "#f1f5f9", mt: 3}}>
					<Container maxWidth={false} sx={{px: {xs: 2, md: 4, lg: 6}}}>
						<Box
							ref={monthBarRef}
							sx={{
								display: "flex",
								flexWrap: "wrap",
								gap: 1,
								py: 2,
								alignItems: "center"
							}}
						>
							<Button
								label="All Dates"
								variant="text"
								noDefaultDimensions={true}
								onClick={() => handleMonthChange("All")}
								style={{
									color: monthFilter === "All" ? "#111827" : "#6b7280",
									borderBottom: "2px solid",
									borderColor: monthFilter === "All" ? "#3b82f6" : "transparent",
									borderRadius: 0,
									minWidth: 72,
									fontWeight: 700
								}} />
							{quickMonthOptions.map(option => (
								<Button
									key={option.slug}
									label={option.label}
									variant="text"
									noDefaultDimensions={true}
									onClick={() => handleMonthChange(option.slug)}
									style={{
										color: monthFilter === option.slug ? "#111827" : "#6b7280",
										borderBottom: "2px solid",
										borderColor: monthFilter === option.slug ? "#3b82f6" : "transparent",
										borderRadius: 0,
										minWidth: 72,
										fontWeight: 700
									}} />
							))}
							{overflowMonthOptions.length > 0 && (
								<Box sx={{width: {xs: "100%", md: 220}, ml: {md: "auto"}}}>
									<Select
										options={[
											{label: "More months", value: "__more__"},
											...overflowMonthOptions.map(option => ({label: option.label, value: option.slug}))
										]}
										size="small"
										value={overflowMonthValue}
										onChange={handleOverflowMonthChange}
										style={{...filterControlStyle, width: "100%"}}
									/>
								</Box>
							)}
						</Box>
					</Container>
				</Box>

				{error && (
					<Box sx={{px: {xs: 2, md: 4, lg: 6}, mt: 3}}>
						<Alert severity="error">
							We could not load {headline.toLowerCase()} right now. Please try again shortly.
						</Alert>
					</Box>
				)}

				<Divider sx={{my: 4}} />

				<Box
					sx={{
						px: {xs: 2, md: 4, lg: 6},
						display: {xs: "block", md: "flex"},
						gap: 3,
						alignItems: {md: "stretch"}
					}}
				>
					<Box sx={{width: {md: "60%"}, pr: {md: 2}}}>
						<RaildaysScrollTopButton />

						{loading && (
							<Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2, color: "text.secondary"}}>
								<Loader size={18} />
								<Typography variant="body2">Loading latest {headline.toLowerCase()}...</Typography>
							</Stack>
						)}

						{!loading && !itemsWithDistance.length && (
							<Box sx={{p: {xs: 3, md: 4}, borderRadius: 2, backgroundColor: "#fff", boxShadow: 2}}>
								<Typography align="center">No items match your criteria.</Typography>
							</Box>
						)}

						<Stack spacing={3}>
							{itemsWithDistance.map((item, itemIndex) => {
								const name = resolveItemName(item);
								const image = resolveItemImage(item);
								const descriptionText = resolveItemDescription(item);
								const longText = resolveItemText(item);
								const itemOrgId = resolveItemOrgId(item);
								const railwayMeta = railwaysByOrgId.get(String(itemOrgId || ""));
								const railwayName =
									resolveItemRailwayName(item) ||
									railwayMeta?.name ||
									"";
								const location = resolveItemLocation(item);
								const address = resolveItemAddress(item);
								const range = resolveEventDateRange(item);
								const types = resolveEventTypes(item);
								const externalLink = resolveExternalLink(item);
								const detailLink = resolveItemLink(item);
								const socialLinks = resolveSocialLinks(item);
								const distance = item?.__distance;
								const itemAccentColor = resolveItemAccentColor(item, railwayMeta?.typeColor);
								const itemAccentText = resolveContrastText(itemAccentColor);

								const dateLabel = range.start && range.end ?
									(range.start.getTime() === range.end.getTime() ?
										formatDateLong(range.start) :
										`${formatDateLong(range.start)} - ${formatDateLong(range.end)}`) :
									"";
								const renderKey = `${resolveItemId(item)}-${range.start?.toISOString() || "none"}-${itemIndex}`;

								return (
									<Card
										key={renderKey}
										gap={0}
										px={0}
										py={0}
										style={{
											backgroundColor: "#fff",
											borderRadius: 12,
											boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)",
											border: `2px solid ${itemAccentColor}`,
											overflow: "hidden"
										}}>
										{dateLabel && (
											<Box sx={{backgroundColor: itemAccentColor, color: itemAccentText, textAlign: "center", py: 1.5, fontWeight: 700}}>
												<Typography sx={{fontSize: {xs: "1.15rem", md: "1.35rem"}, fontWeight: 900, lineHeight: 1.2}}>
													{dateLabel}
												</Typography>
											</Box>
										)}
										{image && (
											<Image
												alt={name}
												noError={true}
												noLoader={true}
												src={image}
												style={{width: "100%", aspectRatio: "16 / 9"}}
												imgStyles={{width: "100%", height: "100%", display: "block"}} />
										)}
										<CardSection
											display="block"
											px={0}
											py={0}
											style={{padding: CARD_BODY_PADDING}}>
											<Stack
												direction={{xs: "column", md: "row"}}
												spacing={2}
												alignItems={{xs: "flex-start", md: "flex-start"}}
												justifyContent="space-between"
											>
												<Box sx={{pr: {md: 2}, flexGrow: 1}}>
													<Typography variant="h5" sx={{fontWeight: 900, color: "#e2b534", lineHeight: 1.2}}>
														{name}
													</Typography>
													{railwayName && (
														<Typography variant="h6" sx={{fontWeight: 800, color: "#334155", lineHeight: 1.2, mt: 0.25}}>
															{railwayName}
														</Typography>
													)}
													{location && (
														<Typography color="text.secondary" sx={{mt: 0.25}}>
															{location}
														</Typography>
													)}
												</Box>
												<Button label="View on Map" variant="outlined" onClick={() => handleViewOnMap(item)} />
											</Stack>

											{descriptionText && (
												<Typography sx={{mt: 3, fontWeight: 700}}>
													{descriptionText}
												</Typography>
											)}
											{longText && (
												<Typography sx={{mt: 2}}>
													{longText}
												</Typography>
											)}

											<Stack direction={{xs: "column", md: "row"}} spacing={2} sx={{mt: 3}} alignItems="center" justifyContent="space-between">
												{detailLink ?
													(
														<Button label="View More Information" uri={detailLink} variant="contained" />
													) :
													(
														<Box />
													)}

												<Stack direction="row" spacing={1} alignItems="center">
													{socialLinks.twitter && (
														<Link uri={socialLinks.twitter}>
															<IconButton
																icon={XIcon}
																label="X"
																style={{border: "1px solid #e2b534", color: "#e2b534"}} />
														</Link>
													)}
													{socialLinks.facebook && (
														<Link uri={socialLinks.facebook}>
															<IconButton
																icon={FacebookIcon}
																label="Facebook"
																style={{border: "1px solid #e2b534", color: "#e2b534"}} />
														</Link>
													)}
													{socialLinks.instagram && (
														<Link uri={socialLinks.instagram}>
															<IconButton
																icon={InstagramIcon}
																label="Instagram"
																style={{border: "1px solid #e2b534", color: "#e2b534"}} />
														</Link>
													)}
												</Stack>
											</Stack>
										</CardSection>

										<Divider />

										<CardSection
											display="block"
											px={0}
											py={0}
											style={{padding: `16px ${CARD_BODY_PADDING} ${CARD_BODY_PADDING}`}}>
											<Stack spacing={2}>
												{dateLabel && (
													<Stack direction={{xs: "column", md: "row"}} spacing={2}>
														<Box sx={{minWidth: 140}}>
															<Typography variant="subtitle2" color="text.secondary">
																Date{range.start && range.end && range.start.getTime() !== range.end.getTime() ? "s" : ""}
															</Typography>
														</Box>
														<Typography>{dateLabel}</Typography>
													</Stack>
												)}

												{!!types.length && (
													<Stack direction={{xs: "column", md: "row"}} spacing={2}>
														<Box sx={{minWidth: 140}}>
															<Typography variant="subtitle2" color="text.secondary">
																Event Type(s)
															</Typography>
														</Box>
														<Stack spacing={0.5}>
															{types.map((type, typeIndex) => (
																<Typography key={`${resolveTypeSlug(type)}-${typeIndex}`}>
																	{resolveTypeName(type)}
																</Typography>
															))}
														</Stack>
													</Stack>
												)}

												{(address || location) && (
													<Stack direction={{xs: "column", md: "row"}} spacing={2}>
														<Box sx={{minWidth: 140}}>
															<Typography variant="subtitle2" color="text.secondary">
																Location
															</Typography>
														</Box>
														<Typography>{address || location}</Typography>
													</Stack>
												)}

												<Stack direction={{xs: "column", md: "row"}} spacing={2} alignItems={{xs: "flex-start", md: "center"}}>
													<Box sx={{minWidth: 140}}>
														<Typography variant="subtitle2" color="text.secondary">
															Distance
														</Typography>
													</Box>
													{typeof distance === "number" && !Number.isNaN(distance) ?
														(
															<Typography>{formatDistanceMilesKm(distance)}</Typography>
														) :
														targetLocation ?
															(
																<Typography color="text.secondary">Distance unavailable</Typography>
															) :
															(
																<Button
																	label="Use My Location"
																	variant="outlined"
																	size="small"
																	onClick={handleUseLocation}
																	disabled={locationLoading} />
															)}
												</Stack>

												{externalLink && (
													<Stack direction={{xs: "column", md: "row"}} spacing={2}>
														<Box sx={{minWidth: 140}}>
															<Typography variant="subtitle2" color="text.secondary">
																Website
															</Typography>
														</Box>
														<Button label={externalLink} uri={externalLink} variant="text" />
													</Stack>
												)}

												{item?.Contact?.Phone ?
													(
														<Stack direction={{xs: "column", md: "row"}} spacing={2}>
															<Box sx={{minWidth: 140}}>
																<Typography variant="subtitle2" color="text.secondary">
																Phone
																</Typography>
															</Box>
															<Typography>{item?.Contact?.Phone}</Typography>
														</Stack>
													) :
													null}
											</Stack>
										</CardSection>
									</Card>
								);
							})}
						</Stack>
					</Box>

					<Box sx={{width: {md: "40%"}, alignSelf: "stretch"}}>
						<Box
							sx={{
								position: {md: "sticky"},
								top: {md: "var(--raildays-content-top)"},
								height: {md: MAP_HEIGHT},
								minHeight: {md: MAP_HEIGHT}
							}}
						>
							<RaildaysMap
								markers={mapMarkers}
								bounds={mapBounds}
								markerShape="circle"
								legendItems={mapLegendItems}
								height="100%"
								mapRef={mapRef}
								markerRefs={markerRefs}
							/>
						</Box>
					</Box>
				</Box>
			</Container>
		</Box>
	);
};

export default RaildaysEventsView;
